import { ChangeEvent, useContext, useEffect, useState } from "react";
import IOnProcessApiKeyDTO from "../../../../models/DTO/IOnProcessApiKeyDTO";
import IProcessApiKeyDTO from "../../../../models/DTO/IProcessApiKeyDTO";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import IApi_Key from "../../../../models/IApi_Key";
import INotification from "../../../../models/INotification";
import NotificationLevelEnum from "../../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../../models/NotificationPositionEnum";
import OwButton from "../../../shared/OwButton/OwButton";
import OwInputWrapper from "../../../shared/OwInputWrapper/OwInputWrapper";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import "./ApiKeySidebar.scss";
import UserContext from "../../../../contexts/UserContext";
import SupplierContext from "../../../../contexts/SupplierContext";
import { SocketContext } from "../../../../contexts/SocketContext";
import UiContext from "../../../../contexts/UiContext";
import IUiSocketSetup from "../../../../models/IUiSocketSetup";
import { ClickToCopy } from "../../../shared/ClickToCopy/ClickToCopy";

type Props = {
    cancelFn: (refreshData?: boolean) => void;
    apiKey?: IApi_Key;
};

export const ApiKeySidebar = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const [createActive, setCreateActive] = useState(false);
    const [name, setName] = useState(props.apiKey?.name || "");
    const [apiKey, setApiKey] = useState<IApi_Key | null>(null);
    const [processedKey, setProcessedKey] = useState(false);

    const onProcessApiKey = (data: IOnProcessApiKeyDTO) => {
        const notification: INotification = {
            message: "successfully_processed_api_key",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        uiContext.notifyFn(null, notification);

        if (!data.api_secret) {
            props.cancelFn();
        } else {
            setApiKey(data.api_secret ? (data as IApi_Key) : null);
            setProcessedKey(true);
        }

        setCreateActive(false);
    };

    const sockets: IUiSocketSetup[] = [{ endpoint: EndpointsEnum.SuppliersOnProcessApiKey, fn: onProcessApiKey }];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        return () => {
            for (const _socket of sockets) {
                socket.off(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    const cancel = (refreshData?: boolean) => {
        props.cancelFn(refreshData);
    };

    const processApiKey = () => {
        const payload: IProcessApiKeyDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            name,
        };

        if (props.apiKey) {
            payload.api_key = props.apiKey.api_key;
        }

        socket.emit(EndpointsEnum.SuppliersProcessApiKey, payload);

        setCreateActive(true);
    };

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.type === "checked" ? e.target.checked : e.target.value;
        const name = e.target.name;

        switch (name) {
            case "name":
                setName(value);
                break;
        }
    };

    return (
        <div className={"ApiKeySidebar p-2"}>
            <div className={"col-12"}>
                <div className="text-end">
                    <button
                        className="btn btn-danger btn-sm"
                        onClick={() => {
                            cancel();
                        }}>
                        <i className="far fa-times" />
                    </button>
                </div>
                <OwTitle className={"mb-2"} leftIconName={"fad fa-key"} title={"api_key"} />
                <hr />
            </div>

            {apiKey || processedKey ? null : (
                <div className={"row"}>
                    <div className={"col-12"}>
                        <OwLabel title={"name"} />
                        <OwInputWrapper name={"name"} fn={handleChange} value={name} />
                    </div>
                </div>
            )}

            {!apiKey ? null : (
                <div>
                    <div className={"row"}>
                        <div className={"col-12"}>
                            <OwLabel title={"name"} isBold={true} postText={":"} className={"me-2"} />
                            {apiKey.name}
                        </div>
                    </div>

                    <div className={"row"}>
                        <div className={"col-12"}>
                            <OwLabel title={"key"} isBold={true} postText={":"} className={"me-2"} />
                            <div className={"d-inline-flex"}>
                                <ClickToCopy text={apiKey.api_key} />
                            </div>
                        </div>
                    </div>

                    <div className={"row"}>
                        <div className={"col-12 d-flex text-warning"}>
                            <i className={"fad fa-exclamation-triangle me-2"} />
                            <OwLabel title={"api_secret_help_warning"} />
                        </div>
                        <div className={"col-12"}>
                            <OwLabel title={"secret"} isBold={true} postText={":"} className={"me-2"} />
                            <div className={"d-inline-flex"}>
                                <ClickToCopy text={apiKey.api_secret} />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {apiKey || processedKey ? null : (
                <div className={"row mt-2"}>
                    <div className={"col-12"}>
                        <div>
                            <OwButton
                                title={props.apiKey ? "update" : "create"}
                                fn={processApiKey}
                                active={createActive}
                                disableOnActive={true}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
